import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ComponentFactoryResolver, Inject, Injectable, inject, signal } from '@angular/core';

import { Phone as SalesPhone, Plan as SalesPlan } from '@bytel/bytel-sales';
import { CartCheckoutService, LINKED_API_PRODUCT_TYPE, LinkedProduct, ProductRepository } from '@bytel/checkout';

import { URLS } from '@base/classes/btl-constants';
import { BtlHelperService } from '@base/services/btl-helper.service';
import { BytelTagService } from '@base/services/bytel-tag.service';

import { ProductsService } from '@catalog/products.service';
import { JsonCatalog, JsonProduct, PlanPrices } from '@catalog/products/interface/context';
import { Plan } from '@catalog/products/subscription/plan';
import { Faim } from '@catalog/products/subscription/plan/faim';
import { FaimPremium } from '@catalog/products/subscription/plan/faim_premium';

import { CartHelperService } from '@checkout/cart/helper/cart-helper.service';

import { RenewContextService } from '@context/child/renew-context.service';

import { MetaDataService } from '@app/base/services/meta-data.service';
import { BasicObject } from '@app/common/models/services.interface';
import { IDeviceConf } from '@app/modules/product-wall/helpers/IDeviceConf';
import { IShowSection } from '@app/modules/product-wall/helpers/IShowSection';
import { BaseProduct } from '@app/modules/product-wall/services/factory/base-product.class';
import { PhonesStorageService } from '@app/modules/product-wall/services/phones-storage.service';
import { ReviewsService } from '@app/services/reviews.service';

import { ConfigurationService } from '@common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalService } from '@trilogy';
import { Observable, firstValueFrom, from, lastValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class CrossSellPriceService extends BaseProduct {
    public defaultUrlPlan: string = Faim.PLAN_TYPE;
    public wallType: string = 'cross-sell';
    public wallUrl: string = URLS.CROSS_SELL;
    public spaActive: boolean = true;
    public cmsBlockId: string = 'banniere_seo_mur-cross-sell';
    public wallWording: string = "de l'équipement";
    public canShowEdp: boolean = false;
    public wallPlans: string[] = [Faim.PLAN_TYPE, FaimPremium.PLAN_TYPE];
    public seeOtherPlans: string = 'Voir les autres forfaits avec cet équipement';
    public defaultPlanIdType = Faim.PLAN_TYPE;
    public canShowPlans = true;
    public canShowSlider = false;
    public canShowPriceAlone: boolean = true;
    public canShowSection = {
        moreDetails: false,
        clickAndCollect: false,
        priceDetails: true,
    } as IShowSection;
    public device = {
        canShowComparator: false,
        colorchipsClass: false,
        canShowDas: true,
        canShowPricesFrom: false,
    } as IDeviceConf;
    public PHONES_WALL_URL: string = URLS.WALLS.FAIM;
    public cardType: string = 'Faim';
    public showFilters: boolean = false;
    public retour: string = 'Retour vers tous les hotspots';

    public excludeGencode = ['11148', '12965'];
    public productsAddedSignal = signal([]);

    private cartCheckoutService: CartCheckoutService = inject(CartCheckoutService);
    private productRepository: ProductRepository = inject(ProductRepository);
    private reviewsService: ReviewsService = inject(ReviewsService);

    constructor(
        protected config: ConfigurationService,
        protected tagService: BytelTagService,
        protected btlHelper: BtlHelperService,
        protected storage: PhonesStorageService,
        protected modalService: NgbModal,
        protected modalServiceTrilogy: ModalService,
        protected renewContextService: RenewContextService,
        protected cartHelper: CartHelperService,
        protected http: HttpClient,
        protected componentFactoryResolver: ComponentFactoryResolver,
        protected productService: ProductsService,
        @Inject(DOCUMENT) protected document: Document,
        protected metaDataService: MetaDataService,
    ) {
        super(
            config,
            tagService,
            btlHelper,
            storage,
            modalService,
            modalServiceTrilogy,
            renewContextService,
            cartHelper,
            http,
            componentFactoryResolver,
            productService,
            document,
            metaDataService,
        );
    }
    public getPlanTypeArray(urlPlanType?: string | null): string[] {
        return [FaimPremium.PLAN_TYPE, Faim.PLAN_TYPE];
    }

    public goToProductPage(product?: JsonProduct): void {
        const isPro = this.document.defaultView.location.search.includes('pro');
        const url = `${this.getProductUrl(product.url)}${isPro ? '?pro=true' : ''}`;
        this.btlHelper.navigateToExternalURL(url);
    }

    public getProductPricesParams(cartPlan?: JsonProduct): object {
        return {};
    }

    public getProductWallPrices(device: JsonProduct, plan: Plan): PlanPrices {
        let basePrice = 0;
        const price = parseFloat(device.final_price);

        if (typeof device.price === 'string') {
            basePrice = parseFloat(device.price);
        } else {
            basePrice = parseFloat(device.price.toFixed(2));
        }

        const displayXPrice = price !== basePrice ? true : false;

        const retour: PlanPrices = {
            price,
            base_price: basePrice,
            displayXPrice,
        };

        if (device.odr && device.odr.amount) {
            retour.price = price - device.odr.amount > 0 ? price - device.odr.amount : 1;
            retour.odr_rsf = { odr: device.odr.amount };
        }

        return retour;
    }

    /**
     * @description permet de lancer les appels en amont en spa lorsque je click sur ajouter page forfait ou page téléphone
     *
     * @param scheme {Scheme}
     * @returns
     */

    public getCrossSellOption(): Observable<any> {
        return from(this.getUpsellsOrCrossSellsProducts(LINKED_API_PRODUCT_TYPE.UPSELL));
    }

    public getCrossSellAccessory(): Observable<any> {
        return from(this.getUpsellsOrCrossSellsProducts(LINKED_API_PRODUCT_TYPE.CROSSSELL));
    }

    private async getUpsellsOrCrossSellsProducts(type: LINKED_API_PRODUCT_TYPE): Promise<any> {
        const cart = await firstValueFrom(this.cartCheckoutService.cartModelObs);
        if (!cart) {
            return null;
        }

        const plan = cart.getQuote().getProductByType(SalesPlan);
        const phone = cart.getQuote().getProductByType(SalesPhone);
        const baseProduct = type === LINKED_API_PRODUCT_TYPE.UPSELL ? plan : phone;

        const additionalProducts: JsonProduct[] = [];

        const linkedProducts = await lastValueFrom(
            this.productRepository.getLinkedProductAttributes(baseProduct.gencode, type),
        );

        if (!linkedProducts.length) {
            return null;
        }

        this.sortProducts(linkedProducts);

        const jsonCatalogProducts = await lastValueFrom(
            this.productService.getProductsByListGencode(linkedProducts.map((p) => p.gencode).join(','), 'false'),
        );

        this.populateAdditionalProducts(linkedProducts, jsonCatalogProducts, additionalProducts);
        const gencodeList = additionalProducts.map((p) => p.gencode);

        return await this.updateProductsReviews(additionalProducts, gencodeList);
    }

    public sendTags(optionNames?: string[]): void {
        const tags: BasicObject = {
            site: 'Boutique',
            univers: 'Boutique_mobile',
            parcours: 'Acquisition',
            page: 'Cross_selling',
            pageCategorie: 'Commande',
            ligneMarche: 'Mobile',
        };

        if (optionNames && optionNames.length > 0) {
            tags.variablesSpecifiques = { affichageOption: optionNames };
        }
        this.tagService.send(tags);
    }

    private sortProducts(products: LinkedProduct[]): LinkedProduct[] {
        return products.sort((n1, n2) => {
            if (n1.position === n2.position) {
                return 0;
            } else {
                return n1.position < n2.position ? -1 : 1;
            }
        });
    }

    private populateAdditionalProducts(
        products: LinkedProduct[],
        jsonCatalogProducts: JsonCatalog,
        additionalProducts: JsonProduct[],
    ): JsonProduct[] {
        products.forEach((data) => {
            if (jsonCatalogProducts[data.gencode] && !this.excludeGencode.includes(data.gencode)) {
                if (data.essential !== undefined) {
                    jsonCatalogProducts[data.gencode].essential = data.essential;
                }
                additionalProducts.push(jsonCatalogProducts[data.gencode]);
            }
        });
        return additionalProducts;
    }

    private async updateProductsReviews(
        additionalProducts: JsonProduct[],
        gencodeList: string[],
    ): Promise<JsonProduct[]> {
        const skeeperNotes = await lastValueFrom(this.reviewsService.getAverages(gencodeList));
        return additionalProducts.map((p) => {
            p.bazaarvoice_best = 0;
            p.bazaarvoice_rating = null;
            p.bazaarvoice_count = 0;

            if (skeeperNotes?.[p.gencode]) {
                p.bazaarvoice_rating = parseInt(skeeperNotes[p.gencode][0].rate) * 20;
                p.bazaarvoice_count = parseInt(skeeperNotes[p.gencode][0].count);
            }

            return p;
        });
    }
}
